import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { Box, Chip, Stack } from "@mui/material";
import React from "react";

// Mobile player column with Name + Position + Team vs Opponent stacked layout
// Matches the projections mobile player column style
const getMobilePlayerColumn = (): GridColDef => ({
  field: "name",
  headerName: "Player",
  width: 150,
  minWidth: 150,
  headerAlign: "left",
  align: "left",
  renderCell: (params: GridRenderCellParams) => {
    const { name } = params.row;
    // Handle both opponent and matchup fields (different sports use different field names)

    return React.createElement(Stack, {
      sx: {
        overflow: "hidden",
        width: "100%",
        py: 0.5,
      },
    }, [
      // Player name row
      React.createElement(Box, {
        key: "name-row",
        sx: {
          fontSize: "13px",
          fontWeight: 500,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          textTransform: "uppercase",
        },
      }, [
        React.createElement("span", { key: "first", style: { color: "rgba(255, 255, 255, 0.70)" } }, name?.split(" ")[0]),
        " ",
        name?.split(" ")?.slice(1).join(" "),
      ])
    ]);
  },
});

// NBA Statlines columns
export const getNbaStatlineColumns = (): GridColDef[] => [
  {
    field: "name",
    headerName: "Player",
    flex: 0.15,
    headerAlign: "left",
    align: "left",
  },
  {
    field: "team",
    headerName: "Team",
    flex: 0.08,
    headerAlign: "center",
    align: "center",
  },
  // {
  //   field: "matchup",
  //   headerName: "Matchup",
  //   flex: 0.1,
  //   headerAlign: "center",
  //   align: "center",
  // },
  {
    field: "projMinutes",
    headerName: "Proj Min",
    flex: 0.08,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(2),
  },
  {
    field: "expPoints",
    headerName: "Exp Pts",
    flex: 0.08,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(1),
  },
  {
    field: "exp3P",
    headerName: "Exp 3P",
    flex: 0.08,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(1),
  },
  {
    field: "expRebounds",
    headerName: "Exp Reb",
    flex: 0.08,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(1),
  },
  {
    field: "expAssists",
    headerName: "Exp Ast",
    flex: 0.08,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(1),
  },
  {
    field: "expSteals",
    headerName: "Exp Stl",
    flex: 0.08,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(1),
  },
  {
    field: "expBlocks",
    headerName: "Exp Blk",
    flex: 0.08,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(1),
  },
  {
    field: "expTurnovers",
    headerName: "Exp TO",
    flex: 0.08,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(1),
  },
  {
    field: "expPointsReboundsAssists",
    headerName: "Exp PRA",
    flex: 0.08,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(1),
  },
];

// NFL Passing columns
export const getNflPassingColumns = (): GridColDef[] => [
  {
    field: "name",
    headerName: "Player",
    flex: 0.15,
    headerAlign: "left",
    align: "left",
  },
  {
    field: "team",
    headerName: "Team",
    flex: 0.1,
    headerAlign: "center",
    align: "center",
  },
  {
    field: "opponent",
    headerName: "Opp",
    flex: 0.1,
    headerAlign: "center",
    align: "center",
  },
  {
    field: "attempts",
    headerName: "Att",
    flex: 0.1,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(1),
  },
  {
    field: "completions",
    headerName: "Comp",
    flex: 0.1,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(1),
  },
  {
    field: "passYd",
    headerName: "Pass Yds",
    flex: 0.1,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(0),
  },
  {
    field: "touchdowns",
    headerName: "TD",
    flex: 0.1,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(2),
  },
  {
    field: "interceptions",
    headerName: "INT",
    flex: 0.1,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(2),
  },
  {
    field: "fumL",
    headerName: "Fum",
    flex: 0.1,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(2),
  },
];

// NFL Receiving columns
export const getNflReceivingColumns = (): GridColDef[] => [
  {
    field: "name",
    headerName: "Player",
    flex: 0.12,
    headerAlign: "left",
    align: "left",
  },
  {
    field: "team",
    headerName: "Team",
    flex: 0.07,
    headerAlign: "center",
    align: "center",
  },
  {
    field: "opponent",
    headerName: "Opp",
    flex: 0.07,
    headerAlign: "center",
    align: "center",
  },
  {
    field: "targets",
    headerName: "Tgt",
    flex: 0.07,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(1),
  },
  {
    field: "targetPercent",
    headerName: "Tgt %",
    flex: 0.08,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) =>
      params.value != null ? params.value?.toFixed(1) + "%" : null,
  },
  {
    field: "catches",
    headerName: "Rec",
    flex: 0.07,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(1),
  },
  {
    field: "catchPercent",
    headerName: "Catch %",
    flex: 0.08,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) =>
      params.value != null ? params.value?.toFixed(1) + "%" : null,
  },
  {
    field: "recYd",
    headerName: "Rec Yds",
    flex: 0.08,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(0),
  },
  {
    field: "ypc",
    headerName: "YPC",
    flex: 0.07,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(1),
  },
  {
    field: "recTD",
    headerName: "TD",
    flex: 0.07,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(2),
  },
  {
    field: "fumbles",
    headerName: "Fum",
    flex: 0.07,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(2),
  },
];

// NFL Rushing columns
export const getNflRushingColumns = (): GridColDef[] => [
  {
    field: "name",
    headerName: "Player",
    flex: 0.15,
    headerAlign: "left",
    align: "left",
  },
  {
    field: "team",
    headerName: "Team",
    flex: 0.12,
    headerAlign: "center",
    align: "center",
  },
  {
    field: "opponent",
    headerName: "Opp",
    flex: 0.12,
    headerAlign: "center",
    align: "center",
  },
  {
    field: "rush",
    headerName: "Rush",
    flex: 0.12,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(1),
  },
  {
    field: "rushYd",
    headerName: "Rush Yds",
    flex: 0.12,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(0),
  },
  {
    field: "rushTD",
    headerName: "TD",
    flex: 0.12,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(2),
  },
  {
    field: "fumL",
    headerName: "Fum",
    flex: 0.12,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(2),
  },
];

// NHL Skater columns
export const getNhlSkaterColumns = (): GridColDef[] => [
  {
    field: "name",
    headerName: "Player",
    flex: 0.18,
    headerAlign: "left",
    align: "left",
  },
  {
    field: "team",
    headerName: "Team",
    flex: 0.08,
    headerAlign: "center",
    align: "center",
  },
  {
    field: "minutes",
    headerName: "Min",
    flex: 0.1,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(2),
  },
  {
    field: "shots",
    headerName: "Shots",
    flex: 0.12,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(2),
  },
  {
    field: "goals",
    headerName: "Goals",
    flex: 0.12,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(2),
  },
  {
    field: "assists",
    headerName: "Assists",
    flex: 0.12,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(2),
  },
  {
    field: "blocks",
    headerName: "Blocks",
    flex: 0.12,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(2),
  },
];

// NHL Goalie columns
export const getNhlGoalieColumns = (): GridColDef[] => [
  {
    field: "name",
    headerName: "Player",
    flex: 0.25,
    headerAlign: "left",
    align: "left",
  },
  {
    field: "projectedSaves",
    headerName: "Proj Saves",
    flex: 0.2,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(1),
  },
  {
    field: "projectedGoals",
    headerName: "Proj GA",
    flex: 0.2,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(2),
  },
  {
    field: "shutoutPercent",
    headerName: "SO %",
    flex: 0.2,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) =>
      params.value != null ? params.value?.toFixed(1) + "%" : null,
  },
];

// MMA Top Stacks columns
export const getMmaTopStacksColumns = (): GridColDef[] => [
  {
    field: "name",
    headerName: "Fighter",
    flex: 0.12,
    headerAlign: "left",
    align: "left",
  },
  {
    field: "fight",
    headerName: "Fight",
    flex: 0.05,
    type: "number",
    headerAlign: "center",
    align: "center",
  },
  {
    field: "projection",
    headerName: "Proj",
    flex: 0.08,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(1),
  },
  {
    field: "salary",
    headerName: "Salary",
    flex: 0.07,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value != null ? `$${Math.round(params.value).toLocaleString()}` : null,
  },
  {
    field: "winOdds",
    headerName: "Win %",
    flex: 0.06,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value != null ? params.value?.toFixed(1) + "%" : null,
  },
  {
    field: "koWin",
    headerName: "KO Win %",
    flex: 0.07,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value != null ? params.value?.toFixed(1) + "%" : null,
  },
  {
    field: "ownership",
    headerName: "Own %",
    flex: 0.06,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value != null ? params.value?.toFixed(1) + "%" : null,
  },
  {
    field: "topPercent",
    headerName: "Top %",
    flex: 0.06,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value != null ? params.value?.toFixed(2) + "%" : null,
  },
  {
    field: "over100FptsPercent",
    headerName: ">100 Fpts %",
    flex: 0.08,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value != null ? params.value?.toFixed(2) + "%" : null,
  },
];

// NFL Top Stacks columns
export const getNflTopStacksColumns = (): GridColDef[] => [
  {
    field: "team",
    headerName: "Team",
    flex: 0.1,
    headerAlign: "center",
    align: "center",
  },
  {
    field: "opp",
    headerName: "Opp",
    flex: 0.1,
    headerAlign: "center",
    align: "center",
  },
  {
    field: "qbOwn",
    headerName: "QB Own %",
    flex: 0.12,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value != null ? params.value?.toFixed(1) + "%" : null,
  },
  {
    field: "topStack",
    headerName: "Top Stack %",
    flex: 0.12,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value != null ? params.value?.toFixed(2) + "%" : null,
  },
  {
    field: "avgSal",
    headerName: "Avg Sal",
    flex: 0.1,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value != null ? `$${Math.round(params.value).toLocaleString()}` : null,
  },
  {
    field: "topValue",
    headerName: "Top Value",
    flex: 0.1,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(2),
  },
  {
    field: "rushPercent",
    headerName: "Rush %",
    flex: 0.1,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value != null ? params.value?.toFixed(1) + "%" : null,
  },
  {
    field: "rating",
    headerName: "Rating",
    flex: 0.08,
    type: "number",
    headerAlign: "center",
    align: "center",
  },
];

// NHL Top Stacks columns
export const getNhlTopStacksColumns = (): GridColDef[] => [
  {
    field: "team",
    headerName: "Team",
    flex: 0.1,
    headerAlign: "center",
    align: "center",
  },
  {
    field: "line",
    headerName: "Line",
    flex: 0.06,
    type: "number",
    headerAlign: "center",
    align: "center",
  },
  {
    field: "p1",
    headerName: "P1",
    flex: 0.12,
    headerAlign: "left",
    align: "left",
  },
  {
    field: "p2",
    headerName: "P2",
    flex: 0.12,
    headerAlign: "left",
    align: "left",
  },
  {
    field: "p3",
    headerName: "P3",
    flex: 0.12,
    headerAlign: "left",
    align: "left",
  },
  {
    field: "lineSalary",
    headerName: "Line Salary",
    flex: 0.1,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value != null ? `$${Math.round(params.value).toLocaleString()}` : null,
  },
  {
    field: "top2StackPercent",
    headerName: "Top 2 Stack %",
    flex: 0.1,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value != null ? params.value?.toFixed(2) + "%" : null,
  },
  {
    field: "ownershipShare",
    headerName: "Own Share",
    flex: 0.1,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value != null ? params.value?.toFixed(2) + "%" : null,
  },
  {
    field: "leverage",
    headerName: "Leverage",
    flex: 0.08,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(2),
  },
];

// CFB Projections columns
export const getCfbProjectionsColumns = (): GridColDef[] => [
  {
    field: "name",
    headerName: "Player",
    flex: 0.12,
    headerAlign: "left",
    align: "left",
  },
  {
    field: "team",
    headerName: "Team",
    flex: 0.06,
    headerAlign: "center",
    align: "center",
  },
  {
    field: "position",
    headerName: "Pos",
    flex: 0.05,
    headerAlign: "center",
    align: "center",
  },
  {
    field: "salary",
    headerName: "Salary",
    flex: 0.07,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value != null ? `$${Math.round(params.value).toLocaleString()}` : null,
  },
  {
    field: "projection",
    headerName: "Proj",
    flex: 0.06,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(1),
  },
  {
    field: "value",
    headerName: "Value",
    flex: 0.06,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(2),
  },
  {
    field: "attempts",
    headerName: "Att",
    flex: 0.05,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(1),
  },
  {
    field: "completions",
    headerName: "Comp",
    flex: 0.05,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(1),
  },
  {
    field: "passYd",
    headerName: "Pass Yds",
    flex: 0.06,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(0),
  },
  {
    field: "passTD",
    headerName: "Pass TD",
    flex: 0.05,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(2),
  },
  {
    field: "interceptions",
    headerName: "INT",
    flex: 0.05,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(2),
  },
  {
    field: "rush",
    headerName: "Rush",
    flex: 0.05,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(1),
  },
  {
    field: "rushYd",
    headerName: "Rush Yds",
    flex: 0.06,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(0),
  },
  {
    field: "rushTD",
    headerName: "Rush TD",
    flex: 0.05,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(2),
  },
  {
    field: "targets",
    headerName: "Tgt",
    flex: 0.05,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(1),
  },
  {
    field: "catches",
    headerName: "Rec",
    flex: 0.05,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(1),
  },
  {
    field: "recYd",
    headerName: "Rec Yds",
    flex: 0.06,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(0),
  },
  {
    field: "recTD",
    headerName: "Rec TD",
    flex: 0.05,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(2),
  },
  {
    field: "fumL",
    headerName: "Fum",
    flex: 0.05,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(2),
  },
];

// NBA Projections columns
export const getNbaProjectionsColumns = (): GridColDef[] => [
  {
    field: "name",
    headerName: "Player",
    flex: 0.14,
    headerAlign: "left",
    align: "left",
  },
  {
    field: "team",
    headerName: "Team",
    flex: 0.07,
    headerAlign: "center",
    align: "center",
  },
  {
    field: "opponent",
    headerName: "Opp",
    flex: 0.07,
    headerAlign: "center",
    align: "center",
  },
  {
    field: "position",
    headerName: "Pos",
    flex: 0.06,
    headerAlign: "center",
    align: "center",
  },
  {
    field: "fpts",
    headerName: "Fpts",
    flex: 0.08,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(1),
  },
  {
    field: "minutes",
    headerName: "Min",
    flex: 0.08,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(1),
  },
  {
    field: "salary",
    headerName: "Salary",
    flex: 0.09,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value != null ? `$${Math.round(params.value).toLocaleString()}` : null,
  },
  {
    field: "injuryStatus",
    headerName: "Injury",
    flex: 0.1,
    headerAlign: "center",
    align: "center",
    renderCell: (params: GridRenderCellParams) => {
      const value = params.value;
      if (!value) return null;
      
      // Color code based on injury status
      let color = "default";
      if (value === "O" || value === "OUT") {
        color = "error";
      } else if (value === "D" || value === "DTD" || value === "DOUBTFUL") {
        color = "warning";
      } else if (value === "Q" || value === "GTD" || value === "QUESTIONABLE") {
        color = "info";
      } else if (value === "P" || value === "PROBABLE") {
        color = "success";
      }
      
      return React.createElement(Chip, {
        label: value,
        size: "small",
        color: color as any,
        sx: { fontSize: "11px", height: "22px" },
      });
    },
  },
  {
    field: "starting",
    headerName: "Starting",
    flex: 0.08,
    headerAlign: "center",
    align: "center",
  },
  {
    field: "confirmed",
    headerName: "Confirmed",
    flex: 0.08,
    headerAlign: "center",
    align: "center",
  },
];

export const getMlbProjectionsColumns = (): GridColDef[] => [
  {
    field: "name",
    headerName: "Player",
    flex: 0.14,
    headerAlign: "left",
    align: "left",
  },
  {
    field: "team",
    headerName: "Team",
    flex: 0.06,
    headerAlign: "center",
    align: "center",
  },
  {
    field: "position",
    headerName: "Pos",
    flex: 0.05,
    headerAlign: "center",
    align: "center",
  },
  {
    field: "rosterPos",
    headerName: "Roster Pos",
    description: "Roster Position",
    flex: 0.07,
    headerAlign: "center",
    align: "center",
  },
  {
    field: "fpts",
    headerName: "Fpts",
    flex: 0.07,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(1),
  },
  {
    field: "salary",
    headerName: "Salary",
    flex: 0.08,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value != null ? `$${Math.round(params.value).toLocaleString()}` : null,
  },
  {
    field: "value",
    headerName: "Value",
    flex: 0.06,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(1),
  },
  {
    field: "hand",
    headerName: "Hand",
    flex: 0.05,
    headerAlign: "center",
    align: "center",
  },
  {
    field: "order",
    headerName: "Bat Pos.",
    description: "Projected Batting Position",
    flex: 0.06,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => {
      if (params.value == null || params.value === 0) return '-';
      return params.value?.toFixed(0);
    },
  },
  {
    field: "teamTotal",
    headerName: "Team Total",
    description: "Projected Team Total",
    flex: 0.07,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueGetter: (params) => {
      const pos = params.row?.position;
      if (pos === 'P' || pos === 'SP' || pos === 'RP') return null;
      return params.value;
    },
    valueFormatter: (params) => params.value?.toFixed(1),
  },
  {
    field: "hrPercent",
    headerName: "HR %",
    flex: 0.06,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => {
      if (params.value == null) return null;
      return params.value?.toFixed(1) + '%';
    },
  },
  {
    field: "ks",
    headerName: "Ks",
    description: "Strikeouts",
    flex: 0.05,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(0),
  },
  {
    field: "stdev",
    headerName: "Std Dev",
    flex: 0.06,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => params.value?.toFixed(2),
  },
  {
    field: "confirmed",
    headerName: "Confirmed",
    flex: 0.06,
    headerAlign: "center",
    align: "center",
  },
  {
    field: "postponement",
    headerName: "PPD",
    description: "Postponement",
    flex: 0.05,
    type: "number",
    headerAlign: "center",
    align: "center",
    valueFormatter: (params) => {
      if (params.value == null || params.value === 0) return '-';
      return params.value?.toFixed(0);
    },
  },
];

// Helper function to get columns based on sport and type
export const getStatsColumns = (sport: string, type: string): GridColDef[] => {
  const key = `${sport.toLowerCase()}-${type.toLowerCase()}`;
  const columnsMap: Record<string, GridColDef[]> = {
    "nba-projections": getNbaProjectionsColumns(),
    "nba-statlines": getNbaStatlineColumns(),
    "nfl-passing": getNflPassingColumns(),
    "nfl-receiving": getNflReceivingColumns(),
    "nfl-rushing": getNflRushingColumns(),
    "nfl-topstacks": getNflTopStacksColumns(),
    "nhl-skater": getNhlSkaterColumns(),
    "nhl-goalie": getNhlGoalieColumns(),
    "nhl-topstacks": getNhlTopStacksColumns(),
    "mma-topstacks": getMmaTopStacksColumns(),
    "mlb-projections": getMlbProjectionsColumns(),
    "cfb-projections": getCfbProjectionsColumns(),
  };
  return columnsMap[key] || [];
};

// ==================== MOBILE COLUMN CONFIGURATIONS ====================

// Helper to convert a desktop column to mobile (fixed width instead of flex)
const toMobileColumn = (col: GridColDef, width: number): GridColDef => ({
  ...col,
  flex: undefined,
  width,
  minWidth: width,
});

// Helper function to get MOBILE columns based on sport and type
// Uses same columns as desktop but with:
// 1. Enhanced Player column (name + position + team vs opponent stacked)
// 2. Team, opponent, matchup columns removed (consolidated in Player cell)
// 3. Fixed widths instead of flex
export const getStatsMobileColumns = (sport: string, type: string): GridColDef[] => {
  const desktopColumns = getStatsColumns(sport, type);

  if (desktopColumns.length === 0) {
    return [];
  }

  // Start with the enhanced mobile Player column
  const mobileColumns: GridColDef[] = [getMobilePlayerColumn()];

  // Fields to skip - they're consolidated into the Player cell
  const skipFields = ["name", "team"];

  // Add remaining columns (skip fields that are shown in the Player cell)
  desktopColumns.forEach((col) => {
    if (skipFields.includes(col.field)) {
      return; // Skip - already handled in Player column
    }

    // Convert to fixed width based on column type
    let width = 70; // default
    if (col.type === "number") {
      width = 65;
    } else if (col.field.includes("Percent") || col.field.includes("percent")) {
      width = 60;
    }

    mobileColumns.push(toMobileColumn(col, width));
  });

  return mobileColumns;
};
